<template>
  <div class="s-setting-container no-border">
    <div class="chat-item">
      <div class="main-header">
        <div>SmartAC.com Support Chat</div>
        <SaveCancelSection
          v-if="adminRole === 'admin'"
          :cancel="resetPartnerConfig"
          :save="updatePartnerConfig"
          :disabled="!changeCheck"
        />
      </div>
      <TechChat
        :method="method"
        :phone="phone"
        :email="email"
        :onChange="handleChange"
        :errorObj="errorObj"
        :primaryColor="primaryColor"
        :companyName="companyName"
        :logoPreview="logoPreview"
        :partnerSettings="partnerSettings"
      />
    </div>
    <div class="chat-item">
      <div class="main-header">
        <div>Direct Chat and Video</div>
      </div>
      <MyTech
        :isMyTech="isMyTech"
        :onChange="handleChange"
        :partnerSettings="partnerSettings"
        :partnerName="companyName"
        :errorObj="errorObj"
      />
      <div v-if="isMyTech === 'yes'" class="mt-4 width-50">
        <RecipientSetting
          :recipients="emails"
          :email="email"
          :addEmail="addEmail"
          :removeEmail="removeEmail"
          :onChange="handleChange"
          :errorObj="errorObj"
        />
      </div>
    </div>
    <ProcessOverlay :show="updating" text="Saving" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import _ from 'lodash';

import partnerSettingsMixin from '@shared/mixins/partnerSettingsMixin';

import TechChat from '@shared/components/partnerSettings/TechChat.vue';
import MyTech from '@shared/components/partnerSettings/MyTech.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import SaveCancelSection from '@shared/components/setting/shared/SaveCancelSection.vue';

import formatPhoneNumber from '@shared/lib/formatter/formatPhoneNumber';
import SACToggleSwitch from '@shared/components/sac/SACToggleSwitch.vue';
import RecipientSetting from '@shared/components/partnerSettings/shared/RecipientSetting.vue';
import emailValidation from '@shared/lib/errorCatch/emailValidation';

export default {
  name: 'TechChatSection',
  components: {
    TechChat,
    MyTech,
    ProcessOverlay,
    SaveCancelSection,
    SACToggleSwitch,
    RecipientSetting,
  },
  mixins: [partnerSettingsMixin],
  props: {
    partnerSettings: Object,
    currentCorporation: Object,
    update: Function,
  },
  data() {
    const {
      TECH_CHAT_METHOD = 'smac',
      TECH_MYTECH_SETTINGS = 'no',
      PRIMARY_COLOR,
      LOGO_URL,
      MYTECH_REPORT_EMAILS = [],
    } = this.partnerSettings;
    return {
      method: TECH_CHAT_METHOD,
      isMyTech: TECH_MYTECH_SETTINGS,
      logoPreview: LOGO_URL,
      companyName: this.currentCorporation?.displayName || this.currentCorporation?.corporationName,
      primaryColor: PRIMARY_COLOR,
      emails: MYTECH_REPORT_EMAILS,
      email: '',
      errorObj: {},
      updating: false,
    };
  },
  computed: {
    ...mapState({
      contacts: (state) => state.partnerSettings.contacts,
    }),
    changeCheck() {
      const {
        TECH_CHAT_METHOD = 'smac',
        TECH_MYTECH_SETTINGS = 'no',
        MYTECH_REPORT_EMAILS = [],
      } = this.partnerSettings;
      let changes = false;
      if (TECH_CHAT_METHOD !== this.method) {
        changes = true;
      }
      if (TECH_MYTECH_SETTINGS !== this.isMyTech) {
        changes = true;
      }
      if (!_.isEqual(MYTECH_REPORT_EMAILS, this.emails)) {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      return changes;
    },
  },
  methods: {
    handleChange(field, value) {
      if (field === 'isMyTech' && value === 'yes' && this.emails.length === 0) {
        this.emails.push(
          ...this.contacts
            .filter(
              (x) => (x.contactType === 'Business' && x.isPrimary) || x.contactType === 'Support',
            )
            .map((el) => el.email),
        );
      }
      let v = value;
      if (field === 'phone') {
        v = formatPhoneNumber(v);
      }
      this[field] = v;
    },
    addEmail() {
      if (emailValidation(this.email) && !this.emails.includes(this.email)) {
        const newEmails = this.emails.slice();
        newEmails.push(this.email);
        this.emails = newEmails;
        this.email = '';
      }
    },
    removeEmail(email) {
      const newEmails = this.emails.slice();
      const idx = newEmails.indexOf(email);
      newEmails.splice(idx, 1);
      this.emails = newEmails;
    },
    resetPartnerConfig() {
      const {
        TECH_CHAT_METHOD = 'smac',
        TECH_MYTECH_SETTINGS = 'no',
        MYTECH_REPORT_EMAILS = [],
      } = this.partnerSettings;
      this.method = TECH_CHAT_METHOD;
      this.isMyTech = TECH_MYTECH_SETTINGS;
      this.emails = MYTECH_REPORT_EMAILS;
      this.errorObj = {};
    },
    async updatePartnerConfig() {
      const { method, isMyTech, emails } = this;
      const errorObj = {};

      const { SERVICE_REQUEST_SUPPORT_PHONE = '', SERVICE_REQUEST_SUPPORT_EMAIL = '' } =
        this.partnerSettings;
      const contact = this.contacts?.find((x) => x.contactType === 'Support');

      const sphone = contact ? contact.phone : SERVICE_REQUEST_SUPPORT_PHONE;
      const semail = contact ? contact.email : SERVICE_REQUEST_SUPPORT_EMAIL;
      this.errorObj = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
        this.updating = true;
        await this.update({
          TECH_CHAT_METHOD: method,
          TECH_CHAT_PHONE: sphone,
          TECH_CHAT_EMAIL: semail,
          TECH_MYTECH_SETTINGS: isMyTech,
          MYTECH_REPORT_EMAILS: emails,
        });
        this.updating = false;
      }
    },
  },
};
</script>
<style lang="scss">
.chat-item {
  border: 1px solid #c4c5cc;
  border-radius: 4px;
  padding: 15px 25px;
}
</style>
